/**
 * Quiz Settings Component
 *
 * Allows configuring quiz mode settings in the form builder.
 * Supports both Simple (self-paced) and Live (real-time interactive) quiz modes.
 */

import { useState } from 'react';
import type { QuizSettings as QuizSettingsType, QuizMode, LiveQuizSettingsConfig } from '../../types/form';
import { LiveQuizSettings } from './LiveQuizSettings';

interface QuizSettingsProps {
  settings: QuizSettingsType | undefined;
  onChange: (settings: QuizSettingsType) => void;
  locked?: boolean; // When true, quiz mode toggle is disabled (for quiz-type forms)
}

const DEFAULT_LIVE_SETTINGS: LiveQuizSettingsConfig = {
  questionTimeSeconds: 20,
  showQuestionOnPlayerDevice: true,
  enableStreakBonus: true,
  streakBonusMultiplier: 1.2,
  streakThreshold: 3,
  timeBasedScoring: true,
  maxPointsPerQuestion: 1000,
  minPointsPerQuestion: 500,
  showLeaderboardAfterQuestion: true,
  leaderboardSize: 5,
  showAnswerDistribution: true,
  showCorrectAnswer: true,
  allowLateJoin: false,
  autoAdvance: true,
  autoAdvanceDelaySeconds: 5,
  randomizeQuestions: false,
  randomizeOptions: true,
  enableSounds: true,
};

export function QuizSettings({ settings, onChange, locked = false }: QuizSettingsProps) {
  const [showLiveSettings, setShowLiveSettings] = useState(false);

  const currentSettings: QuizSettingsType = settings || {
    enabled: false,
    mode: 'simple',
    showScore: true,
    showScorePercentage: true,
    showCorrectAnswers: true,
    showCorrectAnswersWhen: 'immediately',
    passingScore: 70,
    passingMessage: 'Congratulations! You passed!',
    failingMessage: 'Unfortunately, you did not pass. Try again!',
    showPointsPerQuestion: true,
    randomizeQuestions: false,
    randomizeOptions: false,
    allowRetake: true,
    maxAttempts: 0,
  };

  const updateSettings = (updates: Partial<QuizSettingsType>) => {
    onChange({ ...currentSettings, ...updates });
  };

  const handleModeChange = (mode: QuizMode) => {
    const updates: Partial<QuizSettingsType> = { mode };

    // Initialize live settings when switching to live mode
    if (mode === 'live' && !currentSettings.liveSettings) {
      updates.liveSettings = DEFAULT_LIVE_SETTINGS;
    }

    updateSettings(updates);
  };

  const handleLiveSettingsChange = (liveSettings: LiveQuizSettingsConfig) => {
    updateSettings({ liveSettings });
  };

  const isLiveMode = currentSettings.mode === 'live';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Quiz Mode</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">Turn your form into a scored quiz</p>
        </div>
        <button
          type="button"
          onClick={() => !locked && updateSettings({ enabled: !currentSettings.enabled })}
          disabled={locked}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            currentSettings.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
          } ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}
          title={locked ? 'Quiz mode is enabled for quiz-type forms' : ''}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              currentSettings.enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {currentSettings.enabled && (
        <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          {/* Quiz Type Selection */}
          <div className="space-y-3">
            <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              Quiz Type
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleModeChange('simple')}
                className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                  !isLiveMode
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="font-medium text-sm text-gray-900 dark:text-gray-100">Simple</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Self-paced quiz. Users complete at their own speed.
                </p>
                {!isLiveMode && (
                  <div className="absolute top-2 right-2 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleModeChange('live')}
                className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                  isLiveMode
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium text-sm text-gray-900 dark:text-gray-100">Live</span>
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200 rounded">
                    Interactive
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Real-time quiz with host control and leaderboards.
                </p>
                {isLiveMode && (
                  <div className="absolute top-2 right-2 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Live Mode Settings */}
          {isLiveMode && (
            <div className="space-y-3 p-4 bg-purple-50 dark:bg-purple-900/10 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-medium text-sm text-purple-900 dark:text-purple-100">Live Quiz Settings</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowLiveSettings(!showLiveSettings)}
                  className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
                >
                  {showLiveSettings ? 'Hide' : 'Configure'}
                </button>
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200 rounded-full">
                  {currentSettings.liveSettings?.questionTimeSeconds || 20}s per question
                </span>
                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200 rounded-full">
                  {currentSettings.liveSettings?.maxPointsPerQuestion || 1000} max points
                </span>
                {currentSettings.liveSettings?.enableStreakBonus && (
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200 rounded-full">
                    Streak bonus
                  </span>
                )}
                {currentSettings.liveSettings?.showLeaderboardAfterQuestion && (
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200 rounded-full">
                    Leaderboard
                  </span>
                )}
              </div>

              {showLiveSettings && (
                <LiveQuizSettings
                  settings={currentSettings.liveSettings || DEFAULT_LIVE_SETTINGS}
                  onChange={handleLiveSettingsChange}
                />
              )}

              <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
                After publishing, use the "Start Live Quiz" button to create a game session.
              </p>
            </div>
          )}

          {/* Simple Mode Settings (only shown when in simple mode) */}
          {!isLiveMode && (
            <>
              {/* Score Display */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Score Display
                </h4>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentSettings.showScore}
                    onChange={(e) => updateSettings({ showScore: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show score after submission</span>
                </label>

                {currentSettings.showScore && (
                  <label className="flex items-center gap-2 ml-6">
                    <input
                      type="checkbox"
                      checked={currentSettings.showScorePercentage}
                      onChange={(e) =>
                        updateSettings({ showScorePercentage: e.target.checked })
                      }
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:bg-gray-700"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Show percentage</span>
                  </label>
                )}

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentSettings.showPointsPerQuestion}
                    onChange={(e) =>
                      updateSettings({ showPointsPerQuestion: e.target.checked })
                    }
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show points per question</span>
                </label>
              </div>

              {/* Correct Answers */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Correct Answers
                </h4>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentSettings.showCorrectAnswers}
                    onChange={(e) =>
                      updateSettings({ showCorrectAnswers: e.target.checked })
                    }
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show correct answers</span>
                </label>

                {currentSettings.showCorrectAnswers && (
                  <div className="ml-6">
                    <label className="text-xs text-gray-600 dark:text-gray-400">When to show:</label>
                    <select
                      value={currentSettings.showCorrectAnswersWhen}
                      onChange={(e) =>
                        updateSettings({
                          showCorrectAnswersWhen: e.target.value as
                            | 'immediately'
                            | 'after_deadline'
                            | 'never',
                        })
                      }
                      className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="immediately">Immediately after submission</option>
                      <option value="after_deadline">After deadline passes</option>
                      <option value="never">Never show</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Passing Score */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Passing Score
                </h4>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={currentSettings.passingScore || 0}
                    onChange={(e) =>
                      updateSettings({ passingScore: Number(e.target.value) })
                    }
                    className="w-20 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">% to pass</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Set to 0 for no passing requirement</p>
              </div>

              {/* Messages */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Result Messages
                </h4>

                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Passing message:</label>
                  <input
                    type="text"
                    value={currentSettings.passingMessage || ''}
                    onChange={(e) => updateSettings({ passingMessage: e.target.value })}
                    placeholder="Congratulations! You passed!"
                    className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400">Failing message:</label>
                  <input
                    type="text"
                    value={currentSettings.failingMessage || ''}
                    onChange={(e) => updateSettings({ failingMessage: e.target.value })}
                    placeholder="Unfortunately, you did not pass."
                    className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Randomization */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Randomization
                </h4>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentSettings.randomizeQuestions}
                    onChange={(e) =>
                      updateSettings({ randomizeQuestions: e.target.checked })
                    }
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Randomize question order</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentSettings.randomizeOptions}
                    onChange={(e) =>
                      updateSettings({ randomizeOptions: e.target.checked })
                    }
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Randomize answer options</span>
                </label>
              </div>

              {/* Time Limit */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Time Limit
                </h4>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    value={currentSettings.timeLimitMinutes || 0}
                    onChange={(e) =>
                      updateSettings({
                        timeLimitMinutes: Number(e.target.value) || undefined,
                      })
                    }
                    className="w-20 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">minutes (0 = no limit)</span>
                </div>
              </div>

              {/* Retakes */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Retakes
                </h4>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentSettings.allowRetake}
                    onChange={(e) => updateSettings({ allowRetake: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Allow quiz retake</span>
                </label>

                {currentSettings.allowRetake && (
                  <div className="ml-6 flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      value={currentSettings.maxAttempts || 0}
                      onChange={(e) =>
                        updateSettings({ maxAttempts: Number(e.target.value) })
                      }
                      className="w-20 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">max attempts (0 = unlimited)</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default QuizSettings;
